import React from 'react'
import dynamic from 'next/dynamic'

import { HorizontalAlignment, i18n } from '@thg-commerce/enterprise-core'
import { Image } from '@thg-commerce/gravity-elements'

import {
  CaptionTriggerButton,
  Container,
  CTAButton1,
  CTAButton2,
  CTAContainer,
  CTAWrapperDesktop,
  CTAWrapperMobile,
  Headline,
  OverlayBox,
  OverlayContainer,
  OverlayContent,
  PlayButton,
  PreviewImage,
  PreviewImageContainer,
  Subtitle,
  Title,
  TranscriptTriggerButton,
  VideoContainer,
} from './styles'
import { VideoTranscriptModal } from './VideoTranscriptModal'

const Play = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/PlayDefaultCircle'),
)

const SvgVideoTranscript = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/VideoTranscript'),
)

const SvgVideoCaption = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/VideoCaption'),
)

export interface TranscriptTriggerProps {
  onClick: (e?: React.SyntheticEvent) => void
  isVisible?: Boolean
}

export const TranscriptTrigger = (props: TranscriptTriggerProps) => {
  return (
    <TranscriptTriggerButton
      sizing="regular"
      emphasis="low"
      isVisible
      aria-label="Transcript"
      onClick={(e) => {
        props.onClick(e)
      }}
      data-testid="transcript-trigger"
    >
      <SvgVideoTranscript />
    </TranscriptTriggerButton>
  )
}

export const CaptionTrigger = (props: TranscriptTriggerProps) => {
  return (
    <CaptionTriggerButton
      sizing="regular"
      emphasis="low"
      isVisible
      aria-label={props.isVisible ? 'Caption Off' : 'Caption On'}
      onClick={(e) => {
        props.onClick(e)
      }}
      data-testid="caption-trigger"
    >
      <SvgVideoCaption />
    </CaptionTriggerButton>
  )
}

const useVideoControls = (videoRef, videoContainerRef) => {
  const [
    transcriptTriggerVisible,
    setTranscriptTriggerVisible,
  ] = React.useState(false)
  const [captionTriggerVisible, setCaptionTriggerVisible] = React.useState(
    false,
  )

  React.useEffect(() => {
    const video = videoRef.current
    const videoContainer = videoContainerRef.current

    if (!video || !videoContainer) return

    let hideTimeout: NodeJS.Timeout
    const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
      navigator.userAgent,
    )

    const hideDelay = isMobile ? 300 : 2700

    const showControls = () => {
      setTranscriptTriggerVisible(true)
      setCaptionTriggerVisible(true)
    }

    const startHideTimer = () => {
      clearTimeout(hideTimeout)
      hideTimeout = setTimeout(() => {
        setTranscriptTriggerVisible(false)
        setCaptionTriggerVisible(false)
      }, hideDelay)
    }

    const handleMouseMove = () => {
      showControls()
      startHideTimer()
    }

    const handleMouseLeave = () => {
      clearTimeout(hideTimeout)
      if (!video.paused) {
        setTimeout(() => {
          setTranscriptTriggerVisible(false)
          setCaptionTriggerVisible(false)
        }, 200)
      }
    }

    const handleVideoEnded = () => {
      setTranscriptTriggerVisible(true)
      setCaptionTriggerVisible(true)
    }

    const handleTouchStart = () => {
      showControls()
    }

    const handleTouchEnd = () => {
      clearTimeout(hideTimeout)
      if (!video.paused) {
        setTimeout(() => {
          setTranscriptTriggerVisible(false)
          setCaptionTriggerVisible(false)
        }, 3000)
      }
    }

    const handlePlay = () => {
      showControls()
      startHideTimer()
    }

    videoContainer.addEventListener('mousemove', handleMouseMove)
    videoContainer.addEventListener('mousemove', handleMouseMove)
    videoContainer.addEventListener('mouseleave', handleMouseLeave)
    videoContainer.addEventListener('touchstart', handleTouchStart)
    videoContainer.addEventListener('touchend', handleTouchEnd)
    video.addEventListener('play', handlePlay)
    video.addEventListener('ended', handleVideoEnded)
    return () => {
      videoContainer.removeEventListener('mousemove', handleMouseMove)
      videoContainer.removeEventListener('mouseleave', handleMouseLeave)
      videoContainer.removeEventListener('touchstart', handleTouchStart)
      videoContainer.removeEventListener('touchend', handleTouchEnd)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('ended', handleVideoEnded)
      clearTimeout(hideTimeout)
    }
  }, [videoRef, videoContainerRef])

  return { transcriptTriggerVisible, captionTriggerVisible }
}

const PreviewImageComponent = ({
  previewImage,
  playVideo,
  playVideoI18nText,
  displayVideo,
}) => {
  if (displayVideo || !previewImage) return null

  return (
    <PreviewImageContainer
      isVisible
      data-testid="preview-image-and-play-button"
    >
      <PreviewImage>
        <Image
          src={[
            { url: previewImage.urls.small, breakpoint: 'xs' },
            { url: previewImage.urls.medium, breakpoint: 'sm' },
            { url: previewImage.urls.large, breakpoint: 'md' },
          ]}
          width="100%"
          height="100%"
          alt={previewImage.alt || ''}
          isAmp={false}
        />
        <PlayButton
          onClick={playVideo}
          aria-label={playVideoI18nText}
          data-testid="play-video-button"
        >
          <Play />
        </PlayButton>
      </PreviewImage>
    </PreviewImageContainer>
  )
}

const VideoComponent = ({
  videoRef,
  displayVideo,
  props,
  videoTabIndex,
  captionVisible,
}) => {
  return (
    <VideoContainer
      ref={videoRef}
      width="100%"
      autoPlay={props.autoplayVideo || false}
      muted={props.muteVideo || false}
      controls={!props.hideVideoControl}
      loop
      isVisible={displayVideo}
      tabIndex={videoTabIndex}
      aria-label={props.videoTitle?.text}
      playsInline
      crossOrigin="anonymous"
    >
      {props.videoAssetPathMp4 && (
        <source src={props.videoAssetPathMp4} type="video/mp4" />
      )}
      {props.videoAssetPathWebm && (
        <source src={props.videoAssetPathWebm} type="video/webm" />
      )}
      {captionVisible && (
        <track
          kind="captions"
          src={props.videoCaptionPathVTT}
          srcLang="en"
          label="English"
          default
        ></track>
      )}
      Your browser does not support HTML video.
    </VideoContainer>
  )
}
export interface VideoHeroBannerProps {
  videoTitle: {
    text?: string
    alignment?: HorizontalAlignment
  }
  videoAssetPathMp4?: string
  videoAssetPathWebm?: string
  previewImage?: {
    urls: {
      small: string
      medium: string
      large: string
    }
    alt?: string
  }
  muteVideo?: boolean
  autoplayVideo?: boolean
  className?: string
  transcript?: string
  videoCaptionPathVTT?: string
  videoHeroHeadline?: string
  videoHeroSubtitle?: string
  videoHeroCtaOneAriaLabel?: string
  videoHeroCtaTwoAriaLabel?: string
  videoHeroCtaOneURL?: string
  videoHeroCtaTwoURL?: string
  hideVideoControl?: boolean
  ctaOnVideoMobile?: string
}

export const VideoHeroBanner = (props: VideoHeroBannerProps) => {
  const [displayVideo, setDisplayVideo] = React.useState(!!props.autoplayVideo)
  const [videoTabIndex, setVideoTabIndex] = React.useState(-1)
  const [captionVisible, setVideoCaptionOpen] = React.useState(false)
  const [
    videoTranscriptModalOpen,
    setVideoTranscriptModalOpen,
  ] = React.useState(false)

  const videoRef = React.createRef<HTMLVideoElement>()
  const videoContainerRef = React.createRef<HTMLDivElement>()

  const { transcriptTriggerVisible, captionTriggerVisible } = useVideoControls(
    videoRef,
    videoContainerRef,
  )

  const playVideo = () => {
    setDisplayVideo(true)
    setVideoTabIndex(0)
    videoRef.current?.play()
  }

  const playVideoI18nText = i18n('general.play.video.button.text')

  return (
    <div className={props.className}>
      {props.videoTitle.text && (
        <Title data-testid="video-title" alignment={props.videoTitle.alignment}>
          {props.videoTitle.text}
        </Title>
      )}

      <Container ref={videoContainerRef}>
        <PreviewImageComponent
          previewImage={props.previewImage}
          playVideo={playVideo}
          playVideoI18nText={playVideoI18nText}
          displayVideo={displayVideo}
        />

        <VideoComponent
          videoRef={videoRef}
          displayVideo={displayVideo}
          props={props}
          videoTabIndex={videoTabIndex}
          captionVisible={captionVisible}
        />

        <OverlayContainer>
          <OverlayContent>
            <OverlayBox>
              {props.videoHeroHeadline &&
                renderHeadline(props.videoHeroHeadline)}
              {props.videoHeroSubtitle && (
                <Subtitle>{props.videoHeroSubtitle}</Subtitle>
              )}
              <CTAWrapperDesktop ctaOnVideoMobile={props.ctaOnVideoMobile}>
                {renderCTAButtons(props)}
              </CTAWrapperDesktop>
            </OverlayBox>
          </OverlayContent>
        </OverlayContainer>

        {renderVideoControls({
          transcriptTriggerVisible,
          captionTriggerVisible,
          setVideoTranscriptModalOpen,
          setVideoCaptionOpen,
          captionVisible,
          transcript: props.transcript,
          videoCaptionPathVTT: props.videoCaptionPathVTT,
          hideVideoControl: props.hideVideoControl,
        })}
      </Container>

      {videoTranscriptModalOpen && (
        <VideoTranscriptModal
          i18nText={{
            closeI18nText: {
              closeAriaLabel: i18n('general.modal.close.button.arialabel'),
              closeLabel: i18n('general.modal.close.button.label'),
            },
            title: i18n('general.videoherobanner.transcript.title'),
          }}
          content={props.transcript}
          onClose={() => setVideoTranscriptModalOpen(false)}
        />
      )}
      <CTAWrapperMobile ctaOnVideoMobile={props.ctaOnVideoMobile}>
        {renderCTAButtons(props)}
      </CTAWrapperMobile>
    </div>
  )
}

function renderHeadline(text: string) {
  if (!text) return null
  const sentencesWithPunct = text.match(/[^,.?!]+[,.?!]+/g) || []
  const matchedText = sentencesWithPunct.join('')
  const remainingText = text.slice(matchedText.length).trim()
  if (remainingText) {
    sentencesWithPunct.push(remainingText)
  }

  return sentencesWithPunct.map((sentence, index) => (
    <Headline key={index}>{sentence.trim()}</Headline>
  ))
}

function renderCTAButtons(props: VideoHeroBannerProps) {
  return (
    <CTAContainer>
      {props.videoHeroCtaOneAriaLabel && (
        <CTAButton1
          sizing="regular"
          emphasis="medium"
          renderedAs="a"
          href={props.videoHeroCtaOneURL || ''}
        >
          {props.videoHeroCtaOneAriaLabel}
        </CTAButton1>
      )}
      {props.videoHeroCtaTwoAriaLabel && (
        <CTAButton2
          sizing="regular"
          emphasis="medium"
          renderedAs="a"
          href={props.videoHeroCtaTwoURL || ''}
        >
          {props.videoHeroCtaTwoAriaLabel}
        </CTAButton2>
      )}
    </CTAContainer>
  )
}

function renderVideoControls({
  transcript,
  transcriptTriggerVisible,
  videoCaptionPathVTT,
  captionTriggerVisible,
  hideVideoControl,
  setVideoTranscriptModalOpen,
  setVideoCaptionOpen,
  captionVisible,
}: {
  transcript?: string
  transcriptTriggerVisible: boolean
  videoCaptionPathVTT?: string
  captionTriggerVisible: boolean
  hideVideoControl: boolean | undefined
  setVideoTranscriptModalOpen: (v: boolean) => void
  setVideoCaptionOpen: (v: (prev: boolean) => boolean) => void
  captionVisible: boolean
}) {
  if (hideVideoControl) return null

  return (
    <>
      {transcript && transcriptTriggerVisible && (
        <TranscriptTrigger onClick={() => setVideoTranscriptModalOpen(true)} />
      )}
      {videoCaptionPathVTT && captionTriggerVisible && (
        <CaptionTrigger
          onClick={() => setVideoCaptionOpen((prev) => !prev)}
          isVisible={captionVisible}
        />
      )}
    </>
  )
}
